import classNames from 'classnames';
import dayjs from 'dayjs';
import { Ref, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { MdCalendarToday, MdCheck, MdFlashOn, MdKeyboardArrowDown, MdMenu } from 'react-icons/md';
import { Link } from 'react-router-dom';

import Card from '../../../components/Card';
import { IModalSectionItem } from '../modals/SectionListModal';
import {
  DisplayWindowStatus,
  getDisplayWindowStatus,
  getPickerBounds,
  isHiddenInPreview,
  isInvalidDisplayWindow,
  isoToPickerDate,
  PICKER_STEP_MINUTES,
  pickerDateToIso,
  PreviewMode,
} from '../utils/displayWindow';

const STATUS_BADGE: Record<DisplayWindowStatus, { label: string; className: string }> = {
  'not-started': { label: 'Scheduled', className: 'bg-warning' },
  showing: { label: 'Showing', className: 'bg-success' },
  expired: { label: 'Expired', className: 'bg-base-300' },
};

const optionClass = 'flex items-center w-full gap-3 px-3 py-3 border rounded-xl';
const activeOptionClass = 'border-colorsBrandRed01Primary bg-red-50';
const fieldClass = 'flex items-center gap-3 px-3 border rounded-lg h-11 border-systemGrays04LabelQuaternary';
const formatWall = (iso?: string | null) => dayjs(isoToPickerDate(iso)).format('DD/MM/YYYY HH:mm');

const getSummary = (status: DisplayWindowStatus, startedAt?: string | null, endedAt?: string | null) => {
  if (status === 'expired') return `Ended ${formatWall(endedAt)}`;
  const end = endedAt ? `until ${formatWall(endedAt)}` : 'no end date';
  return status === 'not-started' ? `Starts ${formatWall(startedAt)} · ${end}` : `Live now · ${end}`;
};

interface IPropsEbookSectionItem {
  activeMultipleSelection?: boolean;
  itemData: IModalSectionItem;
  previewMode?: PreviewMode;
  deleteMultipleItem: IModalSectionItem[];
  onDeleteSectionItem?: (item: IModalSectionItem) => void;
  onToggleItem?: (item: IModalSectionItem) => void;
  dragHandleRef?: Ref<HTMLElement>;
  onChangeDisplayWindow?: (item: IModalSectionItem, window: { startedAt: string | null; endedAt: string | null }) => void;
}

const SectionItem = (props: IPropsEbookSectionItem) => {
  const { dragHandleRef, activeMultipleSelection, onDeleteSectionItem, itemData, deleteMultipleItem, onToggleItem, onChangeDisplayWindow, previewMode = { type: 'now' } } = props;
  const status = getDisplayWindowStatus(itemData.startedAt, itemData.endedAt);
  const isHidden = isHiddenInPreview(itemData, previewMode);
  const isInvalid = isInvalidDisplayWindow(itemData.startedAt, itemData.endedAt);
  const badge = STATUS_BADGE[status];
  const summary = getSummary(status, itemData.startedAt, itemData.endedAt);
  const [open, setOpen] = useState(false);
  const [scheduled, setScheduled] = useState(!!(itemData.startedAt || itemData.endedAt));
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => !popoverRef.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const showNow = () => {
    setScheduled(false);
    onChangeDisplayWindow?.(itemData, { startedAt: null, endedAt: null });
  };
  const startLabelId = `window-start-${itemData.id}`;
  const endLabelId = `window-end-${itemData.id}`;
  const errorId = `window-error-${itemData.id}`;
  const invalidMessage = itemData.startedAt ? 'End time must be after start time' : 'End time must be after now';
  const { startMin, startMax, endMin } = getPickerBounds(itemData.startedAt, itemData.endedAt);
  // safety net for a day click that carries the old time-of-day into an out-of-range value
  const clampStart = (date: Date | null) => {
    if (date && date < startMin) return startMin;
    return date && startMax && date >= startMax ? dayjs(startMax).subtract(PICKER_STEP_MINUTES, 'minute').toDate() : date;
  };
  const clampEnd = (date: Date | null) => (date && date <= endMin ? dayjs(endMin).add(PICKER_STEP_MINUTES, 'minute').toDate() : date);
  const datePickerProps = {
    dateFormat: 'dd/MM/yyyy HH:mm',
    timeFormat: 'HH:mm',
    showTimeSelect: true,
    timeIntervals: PICKER_STEP_MINUTES,
    isClearable: true,
    // pick-only: a typed out-of-range value is ignored by minDate/maxDate but its text would stay in the input
    onChangeRaw: (e: React.FocusEvent<HTMLInputElement>) => e?.preventDefault?.(),
    popperProps: { strategy: 'fixed' as const },
  };

  return (
    <Card className={classNames('relative flex flex-col justify-center gap-1 h-full pl-4 pr-2 rounded-0', { 'z-30': open })}>
      <div className="flex items-center">
        <div
          className={classNames('flex items-center min-w-0 flex-1', {
            'opacity-50': status !== 'showing',
            'line-through': isHidden,
          })}
        >
          <div className="w-[32px] shrink-0 flex justify-center items-center">
            {activeMultipleSelection ? (
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={!!deleteMultipleItem.find((item) => item.id === itemData.id)}
                onChange={() => onToggleItem?.(itemData)}
              />
            ) : (
              <span ref={dragHandleRef} className="cursor-grab active:cursor-grabbing" role="img" aria-label="Drag to reorder" title="Drag to reorder">
                <MdMenu size={22} />
              </span>
            )}
          </div>
          {itemData.id ? (
            <Link to={`/feedAndSection/sections/edit?id=${itemData.id}`} className="mr-8 min-w-0 link text-colorsBrandRed01Primary">
              <p className="ml-4 font-medium truncate font-tt" title={itemData?.note || '[no note data]'}>
                {itemData.note}
              </p>
            </Link>
          ) : (
            <p className="ml-4 font-medium truncate font-tt"> {'[no note data]'}</p>
          )}
        </div>

        <button className="underline btn btn-sm btn-error btn-ghost shrink-0 text-colorsBrandRed01Primary" onClick={() => onDeleteSectionItem?.(itemData)}>
          Delete
        </button>
      </div>

      <div className="flex items-center ml-12 space-x-3 text-sm">
        <div ref={popoverRef} className="relative shrink-0">
          <button
            type="button"
            aria-haspopup="true"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className={classNames('flex items-center justify-between w-[122px] h-8 pl-3 pr-2 rounded-full font-semibold text-black', isInvalid ? 'bg-error' : badge.className)}
          >
            {isInvalid ? 'Invalid' : badge.label}
            <MdKeyboardArrowDown size={18} aria-hidden="true" />
          </button>
          {open && (
            <div
              role="group"
              aria-label="Display window"
              className="absolute left-0 z-50 p-2 mt-2 bg-white border rounded-2xl shadow-xl top-full w-[440px] border-systemGrays04LabelQuaternary"
            >
              <button type="button" aria-pressed={!scheduled} onClick={showNow} className={classNames(optionClass, scheduled ? 'border-transparent' : activeOptionClass)}>
                <MdFlashOn size={22} className="text-green-700 shrink-0" aria-hidden="true" />
                <span className="flex-1 text-left">
                  <span className="block font-semibold">Show now</span>
                  <span className="block text-gray-600">Live immediately, no end date</span>
                </span>
                {!scheduled && <MdCheck size={20} className="text-colorsBrandRed01Primary" aria-hidden="true" />}
              </button>
              <button
                type="button"
                aria-pressed={scheduled}
                onClick={() => setScheduled(true)}
                className={classNames(optionClass, scheduled ? activeOptionClass : 'border-transparent')}
              >
                <MdCalendarToday size={20} className="text-amber-700 shrink-0" aria-hidden="true" />
                <span className="flex-1 text-left">
                  <span className="block font-semibold">Schedule</span>
                  <span className="block text-gray-600">Set a start and/or end time</span>
                </span>
                {scheduled && <MdCheck size={20} className="text-colorsBrandRed01Primary" aria-hidden="true" />}
              </button>
              {scheduled && (
                <div className="px-3 pt-3 pb-1 ml-9">
                  <div
                    className={classNames(fieldClass, {
                      'border-error': isInvalid,
                    })}
                  >
                    <span id={startLabelId} className="w-10 text-xs font-semibold text-gray-600">
                      START
                    </span>
                    <DatePicker
                      {...datePickerProps}
                      ariaLabelledBy={startLabelId}
                      ariaInvalid={isInvalid ? 'true' : undefined}
                      ariaDescribedBy={isInvalid ? errorId : undefined}
                      placeholderText="Now"
                      className="w-full font-semibold bg-transparent outline-none"
                      wrapperClassName="flex-1"
                      selected={isoToPickerDate(itemData.startedAt)}
                      minDate={startMin}
                      maxDate={startMax}
                      filterTime={(time: Date) => time >= startMin && (!startMax || time < startMax)}
                      onChange={(date: Date | null) =>
                        onChangeDisplayWindow?.(itemData, {
                          startedAt: pickerDateToIso(clampStart(date)),
                          endedAt: itemData.endedAt ?? null,
                        })
                      }
                    />
                    <MdCalendarToday size={16} aria-hidden="true" />
                  </div>
                  <div
                    className={classNames(fieldClass, 'mt-3', {
                      'border-error': isInvalid,
                    })}
                  >
                    <span id={endLabelId} className="w-10 text-xs font-semibold text-gray-600">
                      END
                    </span>
                    <DatePicker
                      {...datePickerProps}
                      ariaLabelledBy={endLabelId}
                      ariaInvalid={isInvalid ? 'true' : undefined}
                      ariaDescribedBy={isInvalid ? errorId : undefined}
                      placeholderText="Never"
                      className="w-full font-semibold bg-transparent outline-none"
                      wrapperClassName="flex-1"
                      selected={isoToPickerDate(itemData.endedAt)}
                      minDate={endMin}
                      filterTime={(time: Date) => time > endMin}
                      onChange={(date: Date | null) =>
                        onChangeDisplayWindow?.(itemData, {
                          startedAt: itemData.startedAt ?? null,
                          endedAt: pickerDateToIso(clampEnd(date)),
                        })
                      }
                    />
                    <MdCalendarToday size={16} aria-hidden="true" />
                  </div>
                  {isInvalid ? (
                    <p id={errorId} role="alert" className="mt-2 text-error">
                      {invalidMessage}
                    </p>
                  ) : (
                    <p className="mt-2 text-gray-600">Both optional — empty Start = show now, empty End = never ends (Bangkok time)</p>
                  )}
                  <div className="flex justify-end mt-3">
                    <button type="button" className="px-5 py-2 font-semibold text-white bg-black rounded-lg" onClick={() => setOpen(false)}>
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <span className={classNames('min-w-0 truncate whitespace-nowrap text-gray-600', { 'text-error': isInvalid })} title={isInvalid ? invalidMessage : summary}>
          {isInvalid ? invalidMessage : summary}
        </span>
      </div>
    </Card>
  );
};

export default SectionItem;
